/**
 * 💕 COUPLES TAB - Mode Mozart/Jobs/Tesla
 * 
 * Pour les couples qui cherchent des amis couples
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Wine, MapPin, Share2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { api } from '../../../services/api';
import { toast } from 'sonner';
import { COUPLE_INTERESTS } from '../CoupleMode';

function CouplesTab({ customerId, isDarkMode, profile, onShowSetup }) {
  const [coupleProfiles, setCoupleProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  
  const isUserCouple = profile?.relationshipStatus === 'couple' || profile?.relationshipStatus === 'open';
  
  useEffect(() => {
    loadCoupleProfiles();
  }, [customerId]);
  
  const loadCoupleProfiles = async () => {
    try {
      setLoading(true);
      // Utiliser la nouvelle API dédiée aux couples
      const response = await api.getCoupleProfiles(customerId);
      setCoupleProfiles(response?.couples || []);
      
      setEvents([
        {
          id: '1',
          title: '🍷 Apéro entre couples',
          date: 'Samedi 18 Jan • 19h',
          location: 'La Petite Épicerie, Vermelles',
          description: 'Soirée conviviale entre couples du quartier. Apportez quelque chose à grignoter !',
          couples: 2,
          maxCouples: 6,
        },
        {
          id: '2', 
          title: '🎲 Soirée jeux de société',
          date: 'Vendredi 24 Jan • 20h',
          location: 'Chez un membre (Noyelles)',
          description: 'Venez avec vos jeux préférés pour une soirée détendue entre amis !',
          couples: 1,
          maxCouples: 4,
        }
      ]);
    } catch (error) {
      console.error('Erreur chargement couples:', error);
      // Fallback vers l'ancienne méthode si l'API n'est pas disponible
      try {
        const response = await api.getHeartbeatsProfiles(customerId);
        const profiles = response?.profiles || response || [];
        const couples = profiles.filter(p => 
          p.relationshipStatus === 'couple' || p.relationshipStatus === 'open'
        );
        setCoupleProfiles(couples);
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (!isUserCouple) {
    return (
      <div className="text-center py-12">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-6"
        >
          <div className="text-6xl mb-4">💕</div>
          <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Espace réservé aux couples !
          </h3>
          <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Tu es en couple et tu cherches des amis couples pour des soirées ?<br/>
            Mets à jour ton profil pour accéder à cette section !
          </p>
        </motion.div>
        
        <Button
          onClick={onShowSetup}
          className="bg-gradient-to-r from-pink-500 to-purple-500 text-white"
        >
          <Users className="w-5 h-5 mr-2" />
          Passer en mode Couple 💕
        </Button>
        
        <div className={`mt-8 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
          <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            ✨ Ce qui t'attend en mode Couple :
          </h4>
          <ul className={`text-sm space-y-2 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <li className="flex items-center gap-2">
              <span className="text-pink-500">💕</span>
              Trouver des couples sympas près de chez toi
            </li>
            <li className="flex items-center gap-2">
              <span className="text-pink-500">🍷</span>
              Participer à des soirées entre couples
            </li>
            <li className="flex items-center gap-2">
              <span className="text-pink-500">🎉</span>
              Organiser des apéros et activités
            </li>
            <li className="flex items-center gap-2">
              <span className="text-pink-500">🤝</span>
              Se faire de vrais amis dans le quartier
            </li>
          </ul>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  return (
    <div className={`space-y-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="text-center">
        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          💕 Amis Couples
        </h2>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
          {profile.partnerName ? `${profile.displayName} & ${profile.partnerName}` : profile.displayName}, 
          trouvez des couples sympas pour des soirées !
        </p>
      </div>
      
      <div>
        <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🎉 Prochaines soirées
        </h3>
        <div className="space-y-3">
          {events.map(event => (
            <motion.div
              key={event.id}
              className={`rounded-xl overflow-hidden border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              whileHover={{ scale: 1.01 }}
            >
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Wine className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {event.title}
                    </h4>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {event.date} • {event.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {event.couples}/{event.maxCouples}
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      couples
                    </div>
                  </div>
                </div>
                
                {event.description && (
                  <p className={`text-sm mt-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {event.description}
                  </p>
                )}

                <Button
                  className="w-full mt-3 bg-gradient-to-r from-purple-500 to-pink-500"
                  size="sm"
                  onClick={() => toast.success('💕 Inscription enregistrée ! On vous contacte bientôt !')}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Rejoindre ({event.couples}/{event.maxCouples} couples)
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div>
        <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          👥 Couples du quartier
        </h3>
        
        {coupleProfiles.length === 0 ? (
          <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className="text-4xl mb-3">🌟</div>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              Vous êtes les premiers couples de la zone !<br/>
              <span className="text-pink-500">Invitez vos amis couples 😉</span>
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {coupleProfiles.slice(0, 6).map((couple, index) => (
              <motion.div
                key={couple.customerId || index}
                className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center overflow-hidden">
                    {couple.avatarImage ? (
                      <img src={couple.avatarImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">💕</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {couple.displayName}{couple.partnerName ? ` & ${couple.partnerName}` : ''}
                    </h4>
                    {couple.city && (
                      <p className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <MapPin className="w-3 h-3" /> {couple.city}
                      </p>
                    )}
                    {couple.coupleInterests?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {couple.coupleInterests.slice(0, 3).map(interest => (
                          <span key={interest} className="text-xs">
                            {COUPLE_INTERESTS.find(i => i.id === interest)?.emoji || '🎉'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toast.success('💌 Demande d\'ami envoyée !')}
                    className="shrink-0"
                  >
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50' : 'bg-gradient-to-r from-purple-50 to-pink-50'}`}>
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Tu connais des couples sympas ? Invite-les ! 🎉
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: 'Heartbeats - Mode Couples',
                text: 'Rejoins-nous sur Heartbeats pour trouver des amis couples pour des soirées ! 💕',
                url: window.location.href
              });
            } else {
              toast.success('Lien copié ! Partage-le à tes amis couples 💕');
            }
          }}
        >
          <Share2 className="w-4 h-4 mr-2" />
          Inviter des couples
        </Button>
      </div>
    </div>
  );
}

export default CouplesTab;
